import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Images, LayoutTemplate, Package, Plus, Sparkles, Users } from 'lucide-react';
import { api } from '../lib/api.ts';
import { guardarSeleccion } from '../lib/catalogo.ts';
import { useSesion } from '../lib/sesion.ts';
import { Avatar, Boton, BotonCirculo, Cargando, EncabezadoPagina, Tarjeta } from '../componentes/ui.tsx';
import { PopupCampania } from '../componentes/PopupCampania.tsx';
import { BannerCampania } from '../componentes/BannerCampania.tsx';

interface Resumen {
  totalPosts: number;
  clientesActivos: number;
  clientesDormidos: number;
  porDia: { fecha: string; posts: number }[];
  topReferencias: { referencia: string; veces: number }[];
  clientesSinPosts: { id: string; razonSocial: string; ruc: string }[];
}

/** Inicio tipo dashboard "lumin": saludo ligero, hero negro, features lima, listas. */
export function PaginaInicio() {
  const { t } = useTranslation();
  const navegar = useNavigate();
  const { data: sesion, isPending } = useSesion();
  const esStaff = ['ADMIN_EMPRESA', 'OPERADOR'].includes(sesion?.usuario.rol ?? '');

  const resumen = useQuery({
    queryKey: ['admin-analitica'],
    queryFn: () => api<Resumen>('/admin/analitica/resumen?dias=30'),
    enabled: esStaff,
  });
  const catalogo = useQuery({
    queryKey: ['inicio-productos'],
    queryFn: () => api<{ items: { id: string; referencia: string; nombre: string; imagenUrl: string; marcaNombre: string; precioGuardado: { precioAhora: number } | null }[] }>('/catalogo/productos?limit=6'),
    enabled: Boolean(sesion?.cliente),
  });
  const plantillas = useQuery({
    queryKey: ['inicio-plantillas'],
    queryFn: () => api<{ items: { id: string; nombre: string; numProductos: number }[] }>('/catalogo/plantillas'),
    enabled: Boolean(sesion?.cliente),
  });
  const novedades = useQuery({
    queryKey: ['inicio-novedades'],
    queryFn: () =>
      api<{ fecha: string | null; items: { id: string; referencia: string; nombre: string; imagenUrl: string; marcaId: string; marcaNombre: string }[] }>('/catalogo/novedades'),
    enabled: Boolean(sesion?.cliente),
  });
  const galeria = useQuery({
    queryKey: ['inicio-galeria'],
    queryFn: () => api<{ items: { id: string; titulo: string; url: string; tipo: string }[] }>('/galeria?limit=4'),
    enabled: Boolean(sesion?.cliente),
  });

  if (isPending) return <Cargando texto={t('comun.cargando')} />;
  if (!sesion) return null;

  const nombre = sesion.cliente?.nombreNegocio ?? sesion.cliente?.razonSocial ?? sesion.empresa?.nombre ?? '';

  function armarPostNovedades() {
    const items = novedades.data?.items ?? [];
    if (items.length === 0) return;
    guardarSeleccion({ productoIds: items.map((p) => p.id), tipoContenido: 'NUEVO_INGRESO' });
    void navegar({ to: '/plantillas' });
  }
  const productos = catalogo.data?.items ?? [];

  return (
    <main className="mx-auto w-full max-w-[1600px]">
      {sesion.cliente ? <PopupCampania /> : null}

      <EncabezadoPagina
        titulo={t('inicio.saludo', { nombre })}
        acciones={
          sesion.cliente ? null : (
            <Boton variante="contorno" onClick={() => void navegar({ to: '/admin/plantillas' })}>
              <Plus size={16} /> {t('inicio.nuevaPlantilla')}
            </Boton>
          )
        }
      />

      {sesion.cliente ? <BannerCampania seccion="inicio_h" className="mb-4" /> : null}

      {/* ── Bento ───────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-6 xl:grid-cols-12">
        {sesion.cliente ? <BannerCampania seccion="inicio_v" className="md:col-span-3 xl:col-span-3" /> : null}
        {/* Hero negro: métrica principal */}
        <Tarjeta
          tono="negra"
          className="md:col-span-3 xl:col-span-3"
          accion={
            <BotonCirculo aria-label="Abrir" className="h-10 w-10" onClick={() => void navegar({ to: sesion.cliente ? '/productos' : '/admin/analitica', search: sesion.cliente ? { tipoContenido: 'PROMOCION' } : undefined })}>
              <ArrowUpRight size={16} />
            </BotonCirculo>
          }
        >
          <p className="text-sm opacity-60">
            {sesion.cliente ? t('inicio.statProductos') : 'Posts últimos 30 días'}
          </p>
          <p className="mt-1 text-[44px] font-light leading-none">
            {sesion.cliente ? productos.length : (resumen.data?.totalPosts ?? 0)}
          </p>

          <div className="mt-5 grid grid-cols-4 gap-2">
            {(sesion.cliente
              ? [
                  { icono: <Package size={18} />, a: '/productos' },
                  { icono: <LayoutTemplate size={18} />, a: '/marca' },
                  { icono: <Images size={18} />, a: '/galeria' },
                  { icono: <Users size={18} />, a: '/cuenta' },
                ]
              : [
                  { icono: <Package size={18} />, a: '/admin/productos' },
                  { icono: <LayoutTemplate size={18} />, a: '/admin/plantillas' },
                  { icono: <Users size={18} />, a: '/admin/clientes' },
                  { icono: <Sparkles size={18} />, a: '/admin/campanias' },
                ]
            ).map((b) => (
              <BotonCirculo key={b.a} className="h-12 w-12" onClick={() => void navegar({ to: b.a as never, search: b.a === '/productos' ? ({ tipoContenido: 'PROMOCION' } as never) : undefined })}>
                {b.icono}
              </BotonCirculo>
            ))}
          </div>

          {!sesion.cliente ? (
            <>
              <p className="mt-6 text-sm opacity-60">Clientes activos</p>
              <div className="progreso mt-2">
                <span
                  style={{
                    width: `${(() => {
                      const a = resumen.data?.clientesActivos ?? 0;
                      const d = resumen.data?.clientesDormidos ?? 0;
                      return a + d ? (a / (a + d)) * 100 : 0;
                    })()}%`,
                  }}
                />
              </div>
              <p className="mt-2 text-sm">
                <span className="text-lg">{resumen.data?.clientesActivos ?? 0}</span>{' '}
                <span className="opacity-60">activos · {resumen.data?.clientesDormidos ?? 0} dormidos</span>
              </p>
            </>
          ) : null}

          <div className="mt-5 space-y-2">
            {(sesion.cliente
              ? [
                  { k: t('inicio.statPlantillas'), v: plantillas.data?.items.length ?? 0 },
                  { k: t('inicio.statMarcas'), v: sesion.marcas.length },
                ]
              : [
                  { k: 'Referencias top', v: resumen.data?.topReferencias.length ?? 0 },
                  { k: 'Sin posts', v: resumen.data?.clientesSinPosts.length ?? 0 },
                ]
            ).map((f) => (
              <div key={f.k} className="interno flex items-center justify-between px-4 py-3">
                <span className="text-sm opacity-70">{f.k}</span>
                <span className="text-base">{f.v}</span>
              </div>
            ))}
          </div>
        </Tarjeta>

        {/* Columna media: lista de productos / referencias */}
        <Tarjeta
          titulo={sesion.cliente ? t('inicio.novedades') : 'Referencias más promocionadas'}
          subtitulo={sesion.cliente && novedades.data?.fecha ? new Date(novedades.data.fecha).toLocaleDateString() : undefined}
          onAccion={() => (sesion.cliente ? armarPostNovedades() : void navegar({ to: '/admin/analitica' }))}
          className="md:col-span-3 xl:col-span-4"
        >
          {sesion.cliente ? (
            <div className="flex h-full flex-col">
              <ul className="grid flex-1 grid-cols-3 gap-2">
                {(novedades.data?.items ?? []).slice(0, 6).map((p) => (
                  <li key={p.id} className="interno overflow-hidden">
                    <img src={p.imagenUrl} alt={p.nombre} className="aspect-square w-full bg-white object-contain" />
                    <p className="truncate px-2 py-1.5 font-mono text-[10px] text-suave">{p.referencia}</p>
                  </li>
                ))}
                {(novedades.data?.items.length ?? 0) === 0 ? (
                  <li className="col-span-3 py-6 text-sm text-suave">{t('inicio.sinNovedades')}</li>
                ) : null}
              </ul>
              {(novedades.data?.items.length ?? 0) > 0 ? (
                <Boton className="mt-3 w-full" onClick={armarPostNovedades}>
                  <Package size={16} /> {t('inicio.armarPost')}
                </Boton>
              ) : null}
            </div>
          ) : (
            <ul className="space-y-1">
              {(resumen.data?.topReferencias ?? []).slice(0, 5).map((r, i) => (
                <li
                  key={r.referencia}
                  className="flex items-center gap-3 rounded-[20px] px-3 py-2.5"
                  style={i === 0 ? { backgroundColor: 'var(--negro)', color: '#fff' } : undefined}
                >
                  <Avatar nombre={r.referencia} className={i === 0 ? 'bg-white/15 text-white' : ''} />
                  <p className="flex-1 text-[15px] font-medium">{r.referencia}</p>
                  <p className="text-sm">{r.veces} posts</p>
                </li>
              ))}
              {(resumen.data?.topReferencias.length ?? 0) === 0 ? <li className="py-6 text-sm text-suave">Todavía no hay posts generados.</li> : null}
            </ul>
          )}
        </Tarjeta>

        {/* Features lima / negro — sólo admin: el cliente crea posts desde "+ Nuevo post" del menú */}
        {sesion.cliente ? null : (
        <div className="grid grid-cols-3 gap-3 md:col-span-6 xl:col-span-5">
          {([
                { tono: 'acento', titulo: 'Plantillas', sub: 'Diseñar y publicar', a: '/admin/plantillas', s: undefined, icono: <LayoutTemplate size={22} /> },
                { tono: 'negra', titulo: 'Clientes', sub: 'Almacenes, cuentas y pedidos', a: '/admin/clientes', s: undefined, icono: <Package size={22} /> },
                { tono: 'acento', titulo: 'Campañas', sub: 'Pop-up para clientes', a: '/admin/campanias', s: undefined, icono: <Sparkles size={22} /> },
              ] as const
          ).map((f) => (
            <Link
              key={f.titulo}
              to={f.a as never}
              search={f.s as never}
              className={`${f.tono === 'negra' ? 'tarjeta-negra' : 'tarjeta-acento'} flex min-h-44 flex-col justify-between p-4 transition hover:-translate-y-0.5`}
            >
              <span
                className="flex h-12 w-12 items-center justify-center rounded-full"
                style={f.tono === 'negra' ? { backgroundColor: 'var(--color-acento-var)', color: 'var(--color-acento-texto)' } : { backgroundColor: 'var(--negro)', color: '#fff' }}
              >
                {f.icono}
              </span>
              <div>
                <p className="text-[15px] font-medium leading-tight">{f.titulo}</p>
                <p className="text-xs opacity-60">{f.sub}</p>
              </div>
            </Link>
          ))}
        </div>
        )}

        {/* Galería (sólo si hay materiales) / clientes sin posts */}
        {sesion.cliente && (galeria.data?.items.length ?? 0) === 0 ? null : (
        <Tarjeta
          titulo={sesion.cliente ? t('nav.galeria') : 'Clientes que nunca generaron un post'}
          onAccion={() => void navegar({ to: sesion.cliente ? '/galeria' : '/admin/clientes' })}
          className="md:col-span-6 xl:col-span-5"
        >
          {sesion.cliente ? (
            <div className="grid grid-cols-4 gap-2">
              {(galeria.data?.items ?? []).map((m) => (
                <div key={m.id} className="interno aspect-square overflow-hidden">
                  <img src={m.url} alt={m.titulo} className="h-full w-full object-cover" />
                </div>
              ))}
              {(galeria.data?.items.length ?? 0) === 0 ? <p className="col-span-4 py-4 text-sm text-suave">{t('galeria.vacio')}</p> : null}
            </div>
          ) : (
            <ul className="divide-y divide-superficie2">
              {(resumen.data?.clientesSinPosts ?? []).slice(0, 5).map((c) => (
                <li key={c.id} className="flex items-center gap-3 py-2.5">
                  <Avatar nombre={c.razonSocial} />
                  <p className="flex-1 truncate text-[15px]">{c.razonSocial}</p>
                  <span className="tag font-mono">{c.ruc}</span>
                </li>
              ))}
              {(resumen.data?.clientesSinPosts.length ?? 0) === 0 ? <li className="py-4 text-sm text-suave">Todos los clientes han generado posts.</li> : null}
            </ul>
          )}
        </Tarjeta>
        )}

        {/* Marcas */}
        <Tarjeta titulo={t('inicio.marcas')} subtitulo={`${sesion.marcas.length}`} className="md:col-span-6 xl:col-span-7">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {sesion.marcas.map((marca) => (
              <button
                key={marca.id}
                type="button"
                onClick={() =>
                  sesion.cliente
                    ? void navegar({ to: '/productos', search: { tipoContenido: 'PROMOCION', marca: marca.id } as never })
                    : void navegar({ to: '/admin/marcas/$marcaId' as never, params: { marcaId: marca.id } as never })
                }
                className="interno flex flex-col items-center gap-3 px-4 py-5 text-center transition hover:-translate-y-0.5 hover:shadow-[var(--sombra)]"
              >
                {marca.logoUrl ? (
                  <img src={marca.logoUrl} alt={marca.nombre} className="h-16 w-16 rounded-2xl bg-white object-contain p-1.5" />
                ) : (
                  <span
                    className="flex h-16 w-16 items-center justify-center rounded-2xl text-2xl font-medium text-white"
                    style={{ backgroundColor: marca.colorPrimario ?? 'var(--negro)' }}
                  >
                    {marca.nombre.charAt(0)}
                  </span>
                )}
                <span className="w-full truncate text-sm font-medium">{marca.nombre}</span>
              </button>
            ))}
            {sesion.marcas.length === 0 ? <p className="col-span-4 text-sm text-suave">{t('inicio.sinMarcas')}</p> : null}
          </div>
        </Tarjeta>
      </div>
    </main>
  );
}
